import { Injectable } from '@angular/core';
import { AnalyticsAdapter } from '../core/analytics-adapter.interface';
import { AnalyticsFactoryService } from './analytics-factory.service';
import { getEnabledProviders } from '../config/analytics-provider.config';
import { ProviderStatus } from '../models/provider-config.model';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsRegistryService {
  private readonly providers: Map<string, AnalyticsAdapter> = new Map();
  private readonly providerStats: Map<string, {
    eventCount: number;
    errorCount: number;
    lastActivity: Date;
    responseTimes: number[];
  }> = new Map();

  constructor(private factory: AnalyticsFactoryService) {}

  /**
   * Initialize all enabled providers automatically
   */
  async initializeProviders(): Promise<void> {
    console.log('[AnalyticsRegistry] Initializing providers...');
    
    const enabledProviders = getEnabledProviders();
    
    if (enabledProviders.length === 0) {
      console.log('[AnalyticsRegistry] No providers enabled, skipping initialization');
      return;
    }

    const initPromises: Promise<void>[] = [];

    // Initialize all enabled providers dynamically
    for (const providerName of enabledProviders) {
      try {
        const provider = this.factory.createProvider(providerName);
        
        if (!provider) {
          console.error(`[AnalyticsRegistry] Failed to create provider: ${providerName}`);
          continue;
        }

        const initPromise = provider.initialize().then(() => {
          this.providers.set(providerName.toLowerCase(), provider);
          
          // Initialize stats
          this.providerStats.set(providerName.toLowerCase(), {
            eventCount: 0,
            errorCount: 0,
            lastActivity: new Date(),
            responseTimes: []
          });

          console.log(`[AnalyticsRegistry] Provider ${providerName} initialized successfully`);
        });

        initPromises.push(initPromise);
      } catch (error) {
        console.error(`[AnalyticsRegistry] Error creating provider ${providerName}:`, error);
      }
    }

    try {
      await Promise.all(initPromises);
      console.log('[AnalyticsRegistry] All providers initialized successfully');
    } catch (error) {
      console.error('[AnalyticsRegistry] Error initializing providers:', error);
      throw error;
    }
  }

  /**
   * Initialize a single provider (useful for runtime provider addition)
   * @param name - Provider name
   */
  async initializeSingleProvider(name: string): Promise<void> {
    try {
      const provider = this.factory.createProvider(name);
      
      if (!provider) {
        throw new Error(`Failed to create provider: ${name}`);
      }

      await provider.initialize();
      this.providers.set(name.toLowerCase(), provider);
      
      // Initialize stats
      this.providerStats.set(name.toLowerCase(), {
        eventCount: 0,
        errorCount: 0,
        lastActivity: new Date(),
        responseTimes: []
      });

      console.log(`[AnalyticsRegistry] Provider ${name} initialized successfully`);
    } catch (error) {
      console.error(`[AnalyticsRegistry] Error initializing provider ${name}:`, error);
      throw error;
    }
  }

  /**
   * Get a provider instance
   * @param name - Provider name
   * @returns Provider instance or null
   */
  getProvider(name: string): AnalyticsAdapter | null {
    return this.providers.get(name.toLowerCase()) || null;
  }

  /**
   * Get all active providers
   * @returns Array of provider instances
   */
  getAllProviders(): AnalyticsAdapter[] {
    return Array.from(this.providers.values());
  }

  /**
   * Get enabled providers
   * @returns Array of enabled provider instances
   */
  getEnabledProviders(): AnalyticsAdapter[] {
    return this.getAllProviders().filter(provider => provider.isEnabled);
  }

  /**
   * Get providers by names
   * @param names - Array of provider names
   * @returns Array of provider instances
   */
  getProvidersByNames(names: string[]): AnalyticsAdapter[] {
    return names
      .map(name => this.getProvider(name))
      .filter(provider => provider !== null) as AnalyticsAdapter[];
  }

  /**
   * Check if a provider is registered
   * @param name - Provider name
   * @returns True if provider is registered
   */
  isProviderRegistered(name: string): boolean {
    return this.providers.has(name.toLowerCase());
  }

  /**
   * Update provider statistics
   * @param name - Provider name
   * @param eventType - Type of event (success, error, etc.)
   * @param responseTime - Response time in milliseconds
   */
  updateProviderStats(name: string, eventType: 'success' | 'error', responseTime?: number): void {
    const stats = this.providerStats.get(name.toLowerCase());
    if (!stats) return;

    stats.lastActivity = new Date();
    
    if (eventType === 'success') {
      stats.eventCount++;
      if (responseTime !== undefined) {
        stats.responseTimes.push(responseTime);
        // Keep only last 100 response times for average calculation
        if (stats.responseTimes.length > 100) {
          stats.responseTimes.shift();
        }
      }
    } else {
      stats.errorCount++;
    }
  }

  /**
   * Get provider status information
   * @returns Array of provider status objects
   */
  getProvidersStatus(): ProviderStatus[] {
    return Array.from(this.providers.entries()).map(([name, provider]) => {
      const stats = this.providerStats.get(name) || {
        eventCount: 0,
        errorCount: 0,
        lastActivity: new Date(),
        responseTimes: []
      };

      const averageResponseTime = stats.responseTimes.length > 0
        ? stats.responseTimes.reduce((sum, time) => sum + time, 0) / stats.responseTimes.length
        : 0;

      return {
        name: provider.name,
        enabled: provider.isEnabled,
        initialized: provider.isInitialized,
        lastError: provider.getLastError()?.message,
        lastActivity: stats.lastActivity,
        eventCount: stats.eventCount,
        errorCount: stats.errorCount,
        averageResponseTime: Math.round(averageResponseTime)
      };
    });
  }
} 